import '.ad{display:grid;}.ssr-plugin'
import '.ae{grid-template-columns:minmax(0, 1fr);}.ssr-plugin'
import '.ac{gap:1.5rem;}.ssr-plugin'
import { BlogCard } from '@app/components/blocks/card/BlogCard'
import { useCallback, useEffect, useState } from 'react'
import { useClient, useLoading, useQuery } from '@based/sdk/react'
import { ElearningItem } from '@schema/elearningItem'
import { useLocation } from 'wouter'
import { pages } from '@app/util/links'
import { styled } from 'inlines'
import { useTranslations } from '@i18n/useLanguage'
import { useInfiniteScroll } from '@app/util/useInfiniteScroll'
import useDateFormat from '@app/util/useDateFormat'
import { PageLayout } from '../layout/PageLayout'
import { Title } from '@app/components/core/typography/Title'

export const Elearning = () => {
  const t = useTranslations().app.elearningPage
  return (
    <PageLayout>
      <Title.H1 style={{ textAlign: 'center' }}>{t.title}</Title.H1>
      <ElearningItemsList />
    </PageLayout>
  )
}

const PAGE_SIZE = 9

export const ElearningItemsList = () => {
  const t = useTranslations().app.elearningPage
  const dateFormat = useDateFormat()
  const [elearningItems, setElearningItems] = useState<ElearningItem[]>([])
  const [top, setTop] = useState(PAGE_SIZE)
  const [mounted, setMounted] = useState(false)
  const client = useClient()
  const isLoading = useLoading()
  const [, setLocation] = useLocation()
  const dataServerQuery = useQuery('list-elearning-item', { top: PAGE_SIZE })
  const countServerQuery = useQuery('list-elearning-item', { count: true })
  const serverElearningItems: ElearningItem[] = dataServerQuery.data ?? []
  const serverCountResult: { count?: number } | undefined = countServerQuery.data
  const serverTotal = serverCountResult?.count ?? serverElearningItems.length

  const query = useCallback(
    (topParam: number) => {
      const close = client
        .query('list-elearning-item', { top: topParam })
        .subscribe((result) => {
          setElearningItems(result ?? [])
          setMounted(true)
        })
      return () => close()
    },
    [client],
  )

  useEffect(() => {
    setTop(PAGE_SIZE)
    setMounted(false)
    const cleanupQuery = query(PAGE_SIZE)
    return () => {
      cleanupQuery()
    }
  }, [query])

  useEffect(() => {
    if (top === PAGE_SIZE) {
      return
    }
    const cleanup = query(top)
    return () => cleanup()
  }, [top, query])

  const renderedElearningItems = mounted ? elearningItems : serverElearningItems
  const currentTotal = serverTotal

  const { lastItemRef } = useInfiniteScroll({
    isLoading,
    hasMore: renderedElearningItems.length < currentTotal,
    loadMore: () => setTop((prev) => prev + PAGE_SIZE),
    itemCount: renderedElearningItems.length,
  })

  if (isLoading) {
    return <div>{t.loading}</div>
  }

  return (
    <styled.div className="ad ae ac"
      style={{
        '@media (min-width: 800px)': {
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          '& > :nth-child(1)': {
            gridColumn: 'span 2',
          },
        },
      }}
    >
      {renderedElearningItems.map((item: ElearningItem, index: number) => {
        const card = (
          <BlogCard
            title={item.title}
            meta={dateFormat(new Date(item.publishedAt))}
            image={item.images?.[0]?.src}
            onClick={() => setLocation(pages.elearningItem(item.slug))}
          />
        )
        if (index === renderedElearningItems.length - 1) {
          return (
            <span key={item.id} ref={lastItemRef}>
              {card}
            </span>
          )
        }
        return <span key={item.id}>{card}</span>
      })}
    </styled.div>
  )
}
